import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'

import Preview from '@renderer/components/Preview'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { Recorder, RecorderStatus } from 'canvas-record'
import { AVC } from 'media-codecs'
import cogoToast from '@successtar/cogo-toast'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { navigateAfterOutputsReady } from '@renderer/utils/navigateAfterOutputs'

const FlipbookCapture: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, userPreferences, id, gallery, uploadSessionState, logActivity } =
    useSessionStateStore()
  const { getFilter, filters } = useConfigStore()
  const { downloadLocal, cameraSettings } = useSettingsStore()

  // Flipbook specific states
  const [currentTake, setCurrentTake] = useState<number>(0)
  const [, setIsPreparingTake] = useState(true)
  const [isRecording, setIsRecording] = useState(false)
  const [countdown, setCountdown] = useState<number>(10)
  const [showPosePrompt, setShowPosePrompt] = useState(false)
  const [prepCountdown, setPrepCountdown] = useState<number>(5)
  const [capturedVideos, setCapturedVideos] = useState<string[]>([])
  const [canvasDimensions, setCanvasDimensions] = useState<{
    width: number
    height: number
  } | null>(null)

  const recorderRef = useRef<Recorder | null>(null)
  const stopRef = useRef<boolean>(false)

  // Hardcoded config values for flipbook
  const TOTAL_TAKES = 3
  const RECORDING_DURATION = 10 // seconds
  const PREP_DURATION = 5 // seconds between takes
  const TARGET_FPS = 30
  const FRAMES_PER_SECOND = 3 // to get 30 frames total from 10 seconds

  // Countdown hooks
  const prepCountdownHook = useCountdown(PREP_DURATION)
  const recordingCountdownHook = useCountdown(RECORDING_DURATION)

  const selectedFilter = getFilter(userPreferences.filterId || filters[0]._id)

  useEffect(() => {
    uploadSessionState()
    startFlipbookSession()

    return (): void => {
      if (recorderRef.current) {
        recorderRef.current.stop()
      }
      stopRef.current = true
    }
  }, [])

  useEffect(() => {
    if (canvasDimensions) {
      const canvas = document.querySelector('canvas')
      const canvasContext = canvas?.getContext('bitmaprenderer')

      if (canvasContext && canvas) {
        const newRecorder = new Recorder(canvasContext as RenderingContext, {
          name: `dreambooth-${id}-flipbook-${currentTake}`,
          encoderOptions: {
            codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
            bitrate: 2000000
          },
          duration: Infinity,
          download: false,
          frameRate: TARGET_FPS
        })
        recorderRef.current = newRecorder
      }
    }
  }, [])

  const startFlipbookSession = async (): Promise<void> => {
    logActivity('Flipbook session started', {
      totalTakes: TOTAL_TAKES,
      recordingDuration: RECORDING_DURATION
    })

    startNextTake()
  }

  const startNextTake = (): void => {
    if (currentTake >= TOTAL_TAKES) {
      completeSession()
      return
    }

    setIsPreparingTake(true)
    setIsRecording(false)
    setPrepCountdown(PREP_DURATION)

    // Show pose prompt for preparation
    setShowPosePrompt(true)

    // Set up prep countdown
    prepCountdownHook.setRemainingSeconds(PREP_DURATION)
    prepCountdownHook.setOnTick(() => {
      setPrepCountdown(prepCountdownHook.getRemainingSeconds())
    })

    prepCountdownHook.setOnComplete(() => {
      setShowPosePrompt(false)
      startRecording()
    })

    prepCountdownHook.start()
  }

  const startRecording = async (): Promise<void> => {
    if (!recorderRef.current) {
      cogoToast.error('Recorder not ready')
      return
    }

    try {
      setIsPreparingTake(false)
      setIsRecording(true)
      setCountdown(RECORDING_DURATION)

      logActivity(`Starting take ${currentTake + 1}`, {
        takeNumber: currentTake + 1,
        duration: RECORDING_DURATION
      })

      await recorderRef.current.start()

      // Set up recording countdown
      recordingCountdownHook.setRemainingSeconds(RECORDING_DURATION)
      recordingCountdownHook.setOnTick(() => {
        setCountdown(recordingCountdownHook.getRemainingSeconds())
      })

      recordingCountdownHook.setOnComplete(() => {
        stopRecording()
      })

      recordingCountdownHook.start()

      // Record frames at specific intervals to get 30 frames total
      const frameInterval = 1000 / FRAMES_PER_SECOND
      let frameCount = 0

      const recordingInterval = setInterval(async () => {
        if (stopRef.current || !recorderRef.current || frameCount >= 30) {
          clearInterval(recordingInterval)
          return
        }

        if (recorderRef.current.status === RecorderStatus.Recording) {
          try {
            await recorderRef.current.step()
            frameCount++
          } catch (error) {
            console.error('Error recording frame:', error)
          }
        }
      }, frameInterval)

      // Store interval reference for cleanup
      setTimeout(() => {
        clearInterval(recordingInterval)
      }, RECORDING_DURATION * 1000)
    } catch (error) {
      console.error('Error starting recording:', error)
      cogoToast.error('Failed to start recording')
    }
  }

  const stopRecording = async (): Promise<void> => {
    if (!recorderRef.current) return

    try {
      setIsRecording(false)

      const recordedChunks = await recorderRef.current.stop()
      const videoBlob = new Blob([recordedChunks as ArrayBuffer], { type: 'video/mp4' })
      const videoUrl = URL.createObjectURL(videoBlob)

      if (downloadLocal) {
        const a = document.createElement('a')
        a.href = videoUrl
        a.download = `dreambooth-${id}-flipbook-${currentTake + 1}-${Date.now()}.mp4`
        a.setAttribute('data-automated-click', 'true')
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)

        logActivity(`Downloaded video`, {
          type: 'video',
          filename: `dreambooth-${id}-flipbook-${currentTake + 1}-${Date.now()}.mp4`
        })
      }

      // Add to captured videos
      setCapturedVideos((prev) => [...prev, videoUrl])

      // Add to session state
      const captureId = `${id}-flipbook-${currentTake + 1}-${Date.now()}`
      const prevItems = gallery.capturedItems || []

      setSessionState({
        gallery: {
          capturedItems: [
            ...prevItems,
            {
              id: captureId,
              photoUrl: '',
              recordingUrl: videoUrl,
              timestamp: new Date()
            }
          ]
        }
      })

      logActivity(`Completed take ${currentTake + 1}`, {
        takeNumber: currentTake + 1,
        captureId,
        totalFrames: 30
      })

      // Move to next take
      setCurrentTake((prev) => prev + 1)

      // Small delay before next take
      setTimeout(() => {
        startNextTake()
      }, 1000)
    } catch (error) {
      console.error('Error stopping recording:', error)
      cogoToast.error('Failed to save recording')
    }
  }

  const completeSession = (): void => {
    logActivity('Flipbook session completed', {
      totalTakes: TOTAL_TAKES,
      capturedVideos: capturedVideos.length
    })

    uploadSessionState()
    navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
  }

  // Call all hooks at the top level
  const cachedFlipbookBg = useCachedBackgroundImage('/assets/boothndaughter/take-flipbook.webp')
  const cachedPoseBg = useCachedBackgroundImage('/assets/boothndaughter/pose.webp')

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        backgroundImage: `url(${cachedFlipbookBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Pose Prompt */}
      {showPosePrompt && (
        <div className="absolute left-1/4 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <div
            style={{
              width: '300px',
              height: '300px',
              backgroundImage: `url(${cachedPoseBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
          <div className="text-center mt-4">
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: '24px',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}
            >
              Get Ready: {prepCountdown}s
            </span>
          </div>
        </div>
      )}

      {/* Preview Area */}
      <div
        className="absolute"
        style={{
          top: '116px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '742px',
          height: '492px'
        }}
      >
        <Preview
          key={`${selectedFilter?._id}-${cameraSettings.mirror}-${cameraSettings.previewRotation}-${cameraSettings.captureRatio}`}
          adjustments={selectedFilter?.adjustments}
          ratio={492 / 742} // landscape ratio
          onCanvasReady={setCanvasDimensions}
          cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
          filter={selectedFilter?._id}
        />
      </div>

      {/* Countdown Display - Right Center */}
      {isRecording && (
        <div className="absolute right-1/4 top-1/2 transform translate-x-1/2 -translate-y-1/2">
          <div className="text-center">
            <span
              style={{
                fontFamily: 'monospace',
                fontSize: '72px',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                fontWeight: 'bold'
              }}
            >
              {countdown}
            </span>
          </div>
        </div>
      )}

      {/* Take Progress */}
      <div className="absolute top-8 left-8">
        <span
          style={{
            fontFamily: 'monospace',
            fontSize: '24px',
            color: 'white',
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
          }}
        >
          Take {currentTake + 1} / {TOTAL_TAKES}
        </span>
      </div>

      {/* Recording Indicator */}
      {isRecording && (
        <div className="absolute top-8 right-8 flex items-center gap-3">
          <div className="w-4 h-4 rounded-full bg-red-500 animate-pulse" />
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: '24px',
              color: '#EF4444',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
            }}
          >
            REC
          </span>
        </div>
      )}
    </div>
  )
}

export default FlipbookCapture
